import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Check, Copy, Truck, Store } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import { createOrder } from "@/lib/api";

const field =
  "w-full rounded-2xl border-2 border-ink/10 bg-[#F8FAF5] px-4 py-3 text-ink placeholder-ink/40 outline-none transition-colors focus:border-watermelon";

export default function OrderSection({ products, quantities, setQty, cashtag }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", fulfillment: "pickup",
    address: "", preferred_day: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const selected = useMemo(
    () => products.filter((p) => (quantities[p.id] || 0) > 0 && !p.sold_out),
    [products, quantities]
  );
  const total = useMemo(
    () => selected.reduce((s, p) => s + p.price * (quantities[p.id] || 0), 0),
    [selected, quantities]
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) return toast.error("Add at least one bottle to your order.");
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!form.phone.trim()) return toast.error("Please enter your phone number.");
    if (!form.preferred_day) return toast.error("Please choose a preferred day.");
    if (form.fulfillment === "delivery" && !form.address.trim())
      return toast.error("Please add a delivery address.");

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        fulfillment: form.fulfillment,
        preferred_day: form.preferred_day,
        address: form.fulfillment === "delivery" ? form.address.trim() : null,
        notes: form.notes.trim() || null,
        items: selected.map((p) => ({ product_id: p.id, qty: quantities[p.id] })),
      };
      const res = await createOrder(payload);
      setResult(res);
      toast.success("Order placed! Check the payment instructions.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCash = () => {
    navigator.clipboard?.writeText(result.cashtag);
    toast.success("Cashtag copied!");
  };

  return (
    <section id="order" className="relative mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-28">
      <div className="wc-blob" style={{ background: "#8AC926", width: 320, height: 320, top: 40, left: -100, opacity: 0.25 }} />
      <Reveal>
        <p className="font-script text-3xl text-ocean">02 — place your order</p>
        <h2 className="mt-1 font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tighter text-ink">
          Fuel up.
        </h2>
      </Reveal>

      <div className="relative z-10 mt-10 grid gap-8 lg:grid-cols-[1fr_0.8fr]">
        <AnimatePresence mode="wait">
          {result ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-[2rem] border border-white/60 bg-white/80 p-8 backdrop-blur-xl"
              data-testid="order-success"
            >
              <div className="grid h-14 w-14 place-items-center rounded-full bg-leaf text-white">
                <Check className="h-7 w-7" />
              </div>
              <h3 className="mt-5 font-display text-3xl font-extrabold uppercase tracking-tight text-ink">
                Thanks, {result.name}!
              </h3>
              <p className="mt-2 text-ink/60">Your order is in. Complete payment below and we'll confirm it.</p>

              <div className="mt-6 rounded-2xl bg-leaf/10 p-6">
                <p className="text-sm font-semibold uppercase tracking-wide text-ink/50">Pay with Cash App</p>
                <p className="mt-2 text-lg text-ink">
                  Send <b className="text-watermelon" data-testid="success-total">${result.total.toFixed(2)}</b> to{" "}
                  <button onClick={copyCash} className="inline-flex items-center gap-1 font-bold text-ocean underline" data-testid="success-cashtag">
                    {result.cashtag} <Copy className="h-4 w-4" />
                  </button>
                </p>
                <p className="mt-2 text-sm text-ink/60">
                  Put your name <b>"{result.name}"</b> in the payment note so we can match your order.
                </p>
              </div>

              <button
                onClick={() => { setResult(null); products.forEach((p) => setQty(p.id, 0)); setForm((f) => ({ ...f, notes: "" })); }}
                className="mt-6 rounded-full border-2 border-ink/15 px-6 py-3 text-sm font-semibold text-ink hover:border-ink/40"
                data-testid="new-order-btn"
              >
                Place another order
              </button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={submit}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="rounded-[2rem] border border-white/60 bg-white/70 p-7 sm:p-9 backdrop-blur-xl"
              data-testid="order-form"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold text-ink">Name</label>
                  <input className={`mt-1.5 ${field}`} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="input-name" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-ink">Phone</label>
                  <input className={`mt-1.5 ${field}`} value={form.phone} onChange={set("phone")} placeholder="(407) 000-0000" data-testid="input-phone" />
                </div>
              </div>

              <div className="mt-4">
                <label className="text-sm font-semibold text-ink">Email <span className="font-normal text-ink/40">(for confirmation)</span></label>
                <input type="email" className={`mt-1.5 ${field}`} value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="input-email" />
              </div>

              <div className="mt-6">
                <label className="text-sm font-semibold text-ink">Pickup or delivery?</label>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  {[
                    { v: "pickup", label: "Pickup", icon: Store },
                    { v: "delivery", label: "Orlando Delivery", icon: Truck },
                  ].map(({ v, label, icon: Icon }) => (
                    <button
                      type="button"
                      key={v}
                      onClick={() => setForm((f) => ({ ...f, fulfillment: v }))}
                      data-testid={`fulfillment-${v}`}
                      className={`flex items-center justify-center gap-2 rounded-2xl border-2 py-3.5 text-sm font-semibold transition-colors ${
                        form.fulfillment === v ? "border-watermelon bg-watermelon/10 text-watermelon" : "border-ink/10 text-ink/60"
                      }`}
                    >
                      <Icon className="h-4 w-4" /> {label}
                    </button>
                  ))}
                </div>
              </div>

              {form.fulfillment === "delivery" && (
                <div className="mt-4">
                  <label className="text-sm font-semibold text-ink">Delivery address</label>
                  <input className={`mt-1.5 ${field}`} value={form.address} onChange={set("address")} placeholder="Street, Orlando, FL" data-testid="input-address" />
                </div>
              )}

              <div className="mt-4">
                <label className="text-sm font-semibold text-ink">Preferred day</label>
                <input type="date" className={`mt-1.5 ${field}`} value={form.preferred_day} onChange={set("preferred_day")} data-testid="input-day" />
              </div>

              <div className="mt-4">
                <label className="text-sm font-semibold text-ink">Notes <span className="font-normal text-ink/40">(optional)</span></label>
                <textarea rows={2} className={`mt-1.5 ${field}`} value={form.notes} onChange={set("notes")} placeholder="Anything we should know?" data-testid="input-notes" />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-order-btn"
                className="mt-7 w-full rounded-full bg-watermelon py-4 text-base font-semibold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {submitting ? "Placing order…" : `Place order · $${total.toFixed(2)}`}
              </button>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Order summary */}
        <div className="h-fit lg:sticky lg:top-24 rounded-[2rem] border border-white/60 bg-white/70 p-7 backdrop-blur-xl" data-testid="order-summary">
          <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-ink">Your order</h3>
          <div className="mt-5 space-y-4">
            {products.filter((p) => !p.sold_out).map((p) => (
              <div key={p.id} className="flex items-center gap-3">
                <img src={p.image} alt={p.name} className="h-14 w-auto" />
                <div className="flex-1">
                  <p className="text-sm font-semibold leading-tight text-ink">{p.name}</p>
                  <p className="text-xs text-ink/50">${p.price.toFixed(2)} each</p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border-2 border-ink/10 p-1">
                  <button onClick={() => setQty(p.id, Math.max(0, (quantities[p.id] || 0) - 1))} className="grid h-7 w-7 place-items-center rounded-full hover:bg-ink/5" data-testid={`summary-minus-${p.id}`}>
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-5 text-center text-sm font-bold" data-testid={`summary-qty-${p.id}`}>{quantities[p.id] || 0}</span>
                  <button onClick={() => setQty(p.id, (quantities[p.id] || 0) + 1)} className="grid h-7 w-7 place-items-center rounded-full bg-watermelon text-white" data-testid={`summary-plus-${p.id}`}>
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between border-t border-ink/10 pt-5">
            <span className="text-sm font-semibold uppercase tracking-wide text-ink/50">Total</span>
            <span className="font-display text-3xl font-extrabold text-watermelon" data-testid="order-total">${total.toFixed(2)}</span>
          </div>
          <p className="mt-4 rounded-2xl bg-ocean/10 p-4 text-xs text-ink/60">
            Payment via Cash App to <b className="text-ocean">{cashtag}</b>. Instructions appear after you place your order.
          </p>
        </div>
      </div>
    </section>
  );
}
